<template>
    <div class="content-wrapper">
        <div class="report-template-settings-shell">
            <div class="settings-header mb-4">
                <h4>Report Template Settings</h4>
                <p class="text-muted">Choose which template will be used when generating the Full Screening Report.</p>
            </div>

            <div class="card shadow-sm">
                <div class="card-body">
                    <label class="form-label font-weight-bold mb-3 d-block">
                        Select Report Template
                    </label>

                    <div class="template-grid">
                        <div
                            v-for="option in templateOptions"
                            :key="option.value"
                            class="template-card"
                            :class="{ selected: selectedTemplate === option.value }"
                            @click="openPreview(option)"
                        >
                            <!-- Selected badge -->
                            <div class="selected-badge" v-if="selectedTemplate === option.value">
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                    <circle cx="9" cy="9" r="9" fill="#4a7cf6"/>
                                    <path d="M5 9l3 3 5-5" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>

                            <!-- Eye / zoom hint -->
                            <div class="preview-hint">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                                    <line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/>
                                </svg>
                                Preview
                            </div>

                            <!-- Preview thumbnail -->
                            <div class="template-preview">
                                <component :is="option.previewComponent" />
                            </div>

                            <!-- Info -->
                            <div class="template-footer">
                                <div class="template-name">{{ option.label }}</div>
                                <div class="template-desc">{{ option.description }}</div>
                            </div>
                        </div>
                    </div>

                    <div class="mt-4 d-flex align-items-center">
                        <button
                            class="btn btn-primary"
                            :disabled="saving"
                            @click="saveTemplate"
                        >
                            <span v-if="saving">Saving...</span>
                            <span v-else>Save</span>
                        </button>
                        <span v-if="savedMessage" class="ml-3 text-success">
                            ✓ {{ savedMessage }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- ── Preview Modal ── -->
        <transition name="modal-fade">
            <div v-if="previewModal.open" class="tpl-modal-backdrop" @click.self="closePreview">
                <div class="tpl-modal">

                    <!-- Modal header -->
                    <div class="tpl-modal-header">
                        <div>
                            <div class="tpl-modal-title">{{ previewModal.option.label }}</div>
                            <div class="tpl-modal-desc">{{ previewModal.option.description }}</div>
                        </div>
                        <button class="tpl-modal-close" @click="closePreview">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                        </button>
                    </div>

                    <!-- Real template preview in iframe -->
                    <div class="tpl-modal-preview">
                        <iframe
                            v-if="previewModal.open"
                            :src="previewUrl"
                            class="tpl-modal-iframe"
                            title="Template Preview"
                            sandbox="allow-same-origin allow-scripts"
                        ></iframe>
                        <div v-else class="tpl-iframe-loading">Loading preview…</div>
                    </div>

                    <!-- Modal footer actions -->
                    <div class="tpl-modal-footer">
                        <button class="btn btn-outline-secondary btn-sm" @click="closePreview">
                            Close
                        </button>
                        <button
                            class="btn btn-primary btn-sm"
                            @click="selectFromModal(previewModal.option)"
                        >
                            <svg width="14" height="14" viewBox="0 0 18 18" fill="none" style="margin-right:5px;vertical-align:middle">
                                <circle cx="9" cy="9" r="9" fill="#fff" opacity="0.25"/>
                                <path d="M5 9l3 3 5-5" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            {{ selectedTemplate === previewModal.option.value ? 'Selected ✓' : 'Select this template' }}
                        </button>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import {
    SCREENING_REPORT_TEMPLATE_SETTINGS,
    SCREENING_REPORT_TEMPLATE_SETTINGS_GET,
    SCREENING_REPORT_TEMPLATE_PREVIEW,
} from '@/app/Config/ScreeningAppUrl';

// ── Preview SVG components ────────────────────────────────────────────────────

// Standard Report — full-width dark image header, centered logo, blue headings, gray text rows
const PreviewStandard = {
    render(h) {
        return h('svg', { attrs: { viewBox: '0 0 220 150', xmlns: 'http://www.w3.org/2000/svg', width: '100%', height: '100%' } }, [
            // white page background
            h('rect', { attrs: { width: '220', height: '150', fill: '#ffffff' } }),

            // ── Full-width dark header image (reportHeader.jpg) ──
            // base dark bg
            h('rect', { attrs: { x: '0', y: '0', width: '220', height: '30', fill: '#1c2a3a' } }),
            // subtle lighter stripe to mimic the image gradient
            h('rect', { attrs: { x: '0', y: '0', width: '220', height: '30', fill: 'url(#stdHdr)' } }),
            h('defs', {}, [
                h('linearGradient', { attrs: { id: 'stdHdr', x1: '0', y1: '0', x2: '1', y2: '0' } }, [
                    h('stop', { attrs: { offset: '0%',   'stop-color': '#1c2a3a' } }),
                    h('stop', { attrs: { offset: '50%',  'stop-color': '#2e4a6a' } }),
                    h('stop', { attrs: { offset: '100%', 'stop-color': '#1c2a3a' } }),
                ])
            ]),
            // company name text overlay on the header image
            h('rect', { attrs: { x: '60', y: '11', width: '100', height: '8', rx: '2', fill: '#fff', opacity: '0.9' } }),

            // ── Centered company logo below header ──
            h('rect', { attrs: { x: '82', y: '36', width: '56', height: '22', rx: '4', fill: '#f0f0f0', stroke: '#ddd', 'stroke-width': '1' } }),
            // logo inner placeholder lines
            h('rect', { attrs: { x: '92', y: '42', width: '36', height: '4', rx: '1', fill: '#bbb' } }),
            h('rect', { attrs: { x: '98', y: '50', width: '24', height: '3', rx: '1', fill: '#ccc' } }),

            // ── Applicant info rows (h6 gray text) ──
            h('rect', { attrs: { x: '14', y: '66', width: '55', height: '3', rx: '1', fill: '#5b5b5b' } }),
            h('rect', { attrs: { x: '74', y: '66', width: '70', height: '3', rx: '1', fill: '#888' } }),

            h('rect', { attrs: { x: '14', y: '73', width: '65', height: '3', rx: '1', fill: '#5b5b5b' } }),
            h('rect', { attrs: { x: '84', y: '73', width: '45', height: '3', rx: '1', fill: '#888' } }),

            h('rect', { attrs: { x: '14', y: '80', width: '60', height: '3', rx: '1', fill: '#5b5b5b' } }),
            h('rect', { attrs: { x: '78', y: '80', width: '55', height: '3', rx: '1', fill: '#888' } }),

            h('rect', { attrs: { x: '14', y: '87', width: '62', height: '3', rx: '1', fill: '#5b5b5b' } }),
            h('rect', { attrs: { x: '80', y: '87', width: '50', height: '3', rx: '1', fill: '#888' } }),

            // ── Blue heading2 company name section ──
            h('rect', { attrs: { x: '14', y: '98', width: '80', height: '5', rx: '2', fill: '#4c64ad' } }),

            // ── Company detail rows ──
            h('rect', { attrs: { x: '14', y: '107', width: '45', height: '3', rx: '1', fill: '#5b5b5b' } }),
            h('rect', { attrs: { x: '63', y: '107', width: '80', height: '3', rx: '1', fill: '#888' } }),

            h('rect', { attrs: { x: '14', y: '114', width: '38', height: '3', rx: '1', fill: '#5b5b5b' } }),
            h('rect', { attrs: { x: '56', y: '114', width: '65', height: '3', rx: '1', fill: '#888' } }),

            h('rect', { attrs: { x: '14', y: '121', width: '30', height: '3', rx: '1', fill: '#5b5b5b' } }),
            h('rect', { attrs: { x: '48', y: '121', width: '55', height: '3', rx: '1', fill: '#888' } }),

            h('rect', { attrs: { x: '14', y: '128', width: '32', height: '3', rx: '1', fill: '#5b5b5b' } }),
            h('rect', { attrs: { x: '50', y: '128', width: '75', height: '3', rx: '1', fill: '#888' } }),

            // ── Bottom decorative lower-element strip ──
            h('rect', { attrs: { x: '0', y: '140', width: '220', height: '10', fill: '#1c2a3a', opacity: '0.7' } }),
        ]);
    }
};

// Design 1 (full_screening_new) — dark navy + gold accent, Playfair style cards
const PreviewDesign1 = {
    render(h) {
        return h('svg', { attrs: { viewBox: '0 0 220 150', xmlns: 'http://www.w3.org/2000/svg', width: '100%', height: '100%' } }, [
            h('rect', { attrs: { width: '220', height: '150', fill: '#faf8f4' } }),
            // company header row
            h('rect', { attrs: { x: '8', y: '8', width: '80', height: '7', rx: '2', fill: '#1a1a2e' } }),
            h('rect', { attrs: { x: '8', y: '19', width: '55', height: '3', rx: '1', fill: '#c9a84c' } }),
            h('rect', { attrs: { x: '8', y: '26', width: '90', height: '2', rx: '1', fill: '#aaa' } }),
            h('line', { attrs: { x1: '8', y1: '33', x2: '212', y2: '33', stroke: '#e2ddd4', 'stroke-width': '1' } }),
            // logo circle (dark with gold initial)
            h('circle', { attrs: { cx: '200', cy: '18', r: '13', fill: '#1a1a2e' } }),
            h('circle', { attrs: { cx: '200', cy: '18', r: '13', fill: 'none', stroke: '#c9a84c', 'stroke-width': '2' } }),
            h('rect', { attrs: { x: '193', y: '15', width: '14', height: '6', rx: '2', fill: '#c9a84c', opacity: '0.6' } }),
            // certificate dark banner
            h('rect', { attrs: { x: '8', y: '38', width: '204', height: '32', rx: '8', fill: '#1a1a2e' } }),
            h('rect', { attrs: { x: '16', y: '43', width: '35', height: '3', rx: '1', fill: '#c9a84c' } }),
            h('rect', { attrs: { x: '16', y: '50', width: '100', height: '6', rx: '2', fill: '#fff', opacity: '0.9' } }),
            h('rect', { attrs: { x: '16', y: '60', width: '55', height: '2.5', rx: '1', fill: '#c9a84c', opacity: '0.6' } }),
            h('rect', { attrs: { x: '130', y: '44', width: '25', height: '2.5', rx: '1', fill: '#fff', opacity: '0.3' } }),
            h('rect', { attrs: { x: '130', y: '50', width: '30', height: '4', rx: '1', fill: '#fff', opacity: '0.8' } }),
            h('rect', { attrs: { x: '168', y: '44', width: '22', height: '2.5', rx: '1', fill: '#fff', opacity: '0.3' } }),
            h('rect', { attrs: { x: '168', y: '50', width: '28', height: '4', rx: '1', fill: '#fff', opacity: '0.8' } }),
            // section title gold label
            h('rect', { attrs: { x: '8', y: '76', width: '50', height: '3', rx: '1', fill: '#c9a84c' } }),
            // candidate white card
            h('rect', { attrs: { x: '8', y: '82', width: '204', height: '34', rx: '6', fill: '#fff', stroke: '#e2ddd4', 'stroke-width': '1' } }),
            h('rect', { attrs: { x: '14', y: '88', width: '38', height: '3', rx: '1', fill: '#aaa' } }),
            h('rect', { attrs: { x: '14', y: '95', width: '70', height: '5', rx: '2', fill: '#1a1a2e' } }),
            h('rect', { attrs: { x: '14', y: '104', width: '50', height: '3', rx: '1', fill: '#ccc' } }),
            h('rect', { attrs: { x: '118', y: '88', width: '38', height: '3', rx: '1', fill: '#aaa' } }),
            h('rect', { attrs: { x: '118', y: '95', width: '60', height: '4', rx: '2', fill: '#444' } }),
            h('rect', { attrs: { x: '118', y: '103', width: '45', height: '3', rx: '1', fill: '#ccc' } }),
            // second section label + card
            h('rect', { attrs: { x: '8', y: '122', width: '45', height: '3', rx: '1', fill: '#c9a84c' } }),
            h('rect', { attrs: { x: '8', y: '129', width: '204', height: '18', rx: '5', fill: '#fff', stroke: '#e2ddd4', 'stroke-width': '1' } }),
            h('rect', { attrs: { x: '14', y: '134', width: '45', height: '3', rx: '1', fill: '#aaa' } }),
            h('rect', { attrs: { x: '14', y: '140', width: '80', height: '4', rx: '1', fill: '#333' } }),
        ]);
    }
};

// Design 2 (full_screening_design2) — black + #00e87b green accent, brutal/modern
const PreviewDesign2 = {
    render(h) {
        return h('svg', { attrs: { viewBox: '0 0 220 150', xmlns: 'http://www.w3.org/2000/svg', width: '100%', height: '100%' } }, [
            h('rect', { attrs: { width: '220', height: '150', fill: '#fafafa' } }),
            // big bold heading
            h('rect', { attrs: { x: '8', y: '8', width: '70', height: '10', rx: '2', fill: '#0f0f0f' } }),
            h('rect', { attrs: { x: '8', y: '22', width: '50', height: '7', rx: '2', fill: '#0f0f0f' } }),
            h('rect', { attrs: { x: '8', y: '33', width: '35', height: '5', rx: '2', fill: '#00e87b' } }),
            // logo box rotated
            h('rect', { attrs: { x: '185', y: '8', width: '26', height: '26', rx: '5', fill: '#0f0f0f', transform: 'rotate(-3 198 21)' } }),
            h('rect', { attrs: { x: '191', y: '16', width: '14', height: '10', rx: '2', fill: '#00e87b', opacity: '0.7' } }),
            // status strip: 4 dark blocks
            h('rect', { attrs: { x: '8', y: '44', width: '48', height: '18', rx: '3', fill: '#0f0f0f' } }),
            h('rect', { attrs: { x: '10', y: '46', width: '30', height: '3', rx: '1', fill: '#00e87b', opacity: '0.7' } }),
            h('rect', { attrs: { x: '10', y: '52', width: '40', height: '4', rx: '1', fill: '#fff', opacity: '0.9' } }),
            h('rect', { attrs: { x: '61', y: '44', width: '48', height: '18', rx: '3', fill: '#0f0f0f' } }),
            h('rect', { attrs: { x: '63', y: '46', width: '28', height: '3', rx: '1', fill: '#00e87b', opacity: '0.7' } }),
            h('rect', { attrs: { x: '63', y: '52', width: '38', height: '4', rx: '1', fill: '#fff', opacity: '0.9' } }),
            h('rect', { attrs: { x: '114', y: '44', width: '48', height: '18', rx: '3', fill: '#0f0f0f' } }),
            h('rect', { attrs: { x: '116', y: '46', width: '32', height: '3', rx: '1', fill: '#00e87b', opacity: '0.7' } }),
            h('rect', { attrs: { x: '116', y: '52', width: '36', height: '4', rx: '1', fill: '#fff', opacity: '0.9' } }),
            h('rect', { attrs: { x: '167', y: '44', width: '45', height: '18', rx: '3', fill: '#0f0f0f' } }),
            h('rect', { attrs: { x: '169', y: '46', width: '25', height: '3', rx: '1', fill: '#00e87b', opacity: '0.7' } }),
            h('rect', { attrs: { x: '169', y: '52', width: '35', height: '4', rx: '1', fill: '#00e87b' } }),
            // card with black header bar
            h('rect', { attrs: { x: '8', y: '68', width: '204', height: '38', rx: '6', fill: '#fff', stroke: '#0f0f0f', 'stroke-width': '1.5' } }),
            h('rect', { attrs: { x: '8', y: '68', width: '204', height: '14', rx: '6', fill: '#0f0f0f' } }),
            h('rect', { attrs: { x: '8', y: '75', width: '204', height: '7', fill: '#0f0f0f' } }),
            h('rect', { attrs: { x: '14', y: '71', width: '50', height: '4', rx: '1', fill: '#fff', opacity: '0.9' } }),
            h('rect', { attrs: { x: '165', y: '71', width: '35', height: '4', rx: '1', fill: '#00e87b', opacity: '0.9' } }),
            h('rect', { attrs: { x: '14', y: '87', width: '40', height: '3', rx: '1', fill: '#aaa' } }),
            h('rect', { attrs: { x: '14', y: '94', width: '65', height: '4', rx: '1', fill: '#333' } }),
            h('rect', { attrs: { x: '115', y: '87', width: '35', height: '3', rx: '1', fill: '#aaa' } }),
            h('rect', { attrs: { x: '115', y: '94', width: '55', height: '4', rx: '1', fill: '#333' } }),
            // emp block with border
            h('rect', { attrs: { x: '8', y: '112', width: '204', height: '32', rx: '6', fill: '#fff', stroke: '#e0e0e0', 'stroke-width': '1.5' } }),
            h('rect', { attrs: { x: '14', y: '118', width: '80', height: '5', rx: '2', fill: '#0f0f0f' } }),
            h('rect', { attrs: { x: '162', y: '117', width: '40', height: '7', rx: '3', fill: '#0f0f0f' } }),
            h('rect', { attrs: { x: '166', y: '119', width: '32', height: '3', rx: '1', fill: '#00e87b' } }),
            h('rect', { attrs: { x: '14', y: '128', width: '55', height: '3', rx: '1', fill: '#888' } }),
            h('rect', { attrs: { x: '14', y: '135', width: '30', height: '5', rx: '10', fill: 'rgba(0,232,123,0.15)', stroke: '#00e87b', 'stroke-width': '1' } }),
            h('rect', { attrs: { x: '50', y: '135', width: '35', height: '5', rx: '10', fill: 'rgba(96,165,250,0.15)', stroke: '#60a5fa', 'stroke-width': '1' } }),
        ]);
    }
};

// Design 3 (full_screening_v2) — green background, floating rounded white/blush cards, maroon primary
const PreviewDesign3 = {
    render(h) {
        return h('svg', { attrs: { viewBox: '0 0 220 150', xmlns: 'http://www.w3.org/2000/svg', width: '100%', height: '100%' } }, [
            h('defs', {}, [
                h('linearGradient', { attrs: { id: 'progGrad3', x1: '0', y1: '0', x2: '1', y2: '0' } }, [
                    h('stop', { attrs: { offset: '0%', 'stop-color': '#8B0040' } }),
                    h('stop', { attrs: { offset: '100%', 'stop-color': '#1DB954' } }),
                ])
            ]),
            // green background
            h('rect', { attrs: { width: '220', height: '150', fill: '#1DB954' } }),
            // hero blush card floating
            h('rect', { attrs: { x: '10', y: '8', width: '200', height: '52', rx: '8', fill: '#f9eef3' } }),
            // company logo + name
            h('rect', { attrs: { x: '18', y: '14', width: '28', height: '14', rx: '3', fill: '#fff', stroke: '#e0c8d4', 'stroke-width': '1' } }),
            h('rect', { attrs: { x: '50', y: '16', width: '60', height: '5', rx: '2', fill: '#1a1a1a' } }),
            h('rect', { attrs: { x: '50', y: '25', width: '40', height: '3', rx: '1', fill: '#aaa' } }),
            // status badge
            h('rect', { attrs: { x: '148', y: '14', width: '54', height: '12', rx: '6', fill: '#fff', stroke: '#1DB954', 'stroke-width': '1.2' } }),
            h('circle', { attrs: { cx: '156', cy: '20', r: '3', fill: '#1DB954' } }),
            h('rect', { attrs: { x: '162', y: '18', width: '32', height: '3', rx: '1', fill: '#333' } }),
            // candidate name maroon
            h('rect', { attrs: { x: '18', y: '33', width: '100', height: '7', rx: '2', fill: '#8B0040' } }),
            h('rect', { attrs: { x: '18', y: '44', width: '65', height: '3', rx: '1', fill: '#888' } }),
            // meta items row
            h('rect', { attrs: { x: '18', y: '52', width: '20', height: '5', rx: '1', fill: '#555' } }),
            h('rect', { attrs: { x: '44', y: '52', width: '24', height: '5', rx: '1', fill: '#555' } }),
            h('rect', { attrs: { x: '74', y: '52', width: '26', height: '5', rx: '1', fill: '#555' } }),
            h('rect', { attrs: { x: '106', y: '52', width: '22', height: '5', rx: '1', fill: '#555' } }),
            // progress white card
            h('rect', { attrs: { x: '10', y: '65', width: '200', height: '20', rx: '7', fill: '#fff' } }),
            h('rect', { attrs: { x: '16', y: '70', width: '42', height: '4', rx: '2', fill: '#333' } }),
            h('rect', { attrs: { x: '16', y: '78', width: '178', height: '4', rx: '2', fill: '#e8e8e8' } }),
            h('rect', { attrs: { x: '16', y: '78', width: '110', height: '4', rx: '2', fill: 'url(#progGrad3)' } }),
            // section label (white on green)
            h('rect', { attrs: { x: '10', y: '90', width: '32', height: '3', rx: '1', fill: '#8B0040', opacity: '0.9' } }),
            h('rect', { attrs: { x: '10', y: '96', width: '65', height: '6', rx: '2', fill: '#fff' } }),
            // candidate white card
            h('rect', { attrs: { x: '10', y: '106', width: '200', height: '22', rx: '7', fill: '#fff' } }),
            h('circle', { attrs: { cx: '22', cy: '117', r: '7', fill: '#f0e4ed' } }),
            h('rect', { attrs: { x: '34', y: '110', width: '60', height: '4', rx: '1', fill: '#1a1a1a' } }),
            h('rect', { attrs: { x: '34', y: '118', width: '42', height: '3', rx: '1', fill: '#aaa' } }),
            h('rect', { attrs: { x: '132', y: '110', width: '35', height: '3', rx: '1', fill: '#ccc' } }),
            h('rect', { attrs: { x: '132', y: '117', width: '50', height: '4', rx: '1', fill: '#555' } }),
            // address white card
            h('rect', { attrs: { x: '10', y: '132', width: '200', height: '14', rx: '6', fill: '#fff' } }),
            h('rect', { attrs: { x: '16', y: '136', width: '28', height: '3', rx: '1', fill: '#1DB954', opacity: '0.8' } }),
            h('rect', { attrs: { x: '16', y: '142', width: '85', height: '3', rx: '1', fill: '#888' } }),
        ]);
    }
};

// ─────────────────────────────────────────────────────────────────────────────

export default {
    name: 'ReportTemplateSettings',
    components: { PreviewStandard, PreviewDesign1, PreviewDesign2, PreviewDesign3 },
    data() {
        return {
            selectedTemplate: 'full_screening',
            saving: false,
            savedMessage: '',
            previewModal: {
                open: false,
                option: null,
            },            templateOptions: [
                {
                    value: 'full_screening',
                    label: 'Standard Report',
                    description: 'Classic layout with dark header bar.',
                    previewComponent: 'PreviewStandard',
                },
                {
                    value: 'full_screening_new',
                    label: 'Design 1',
                    description: 'Blue sidebar with structured content.',
                    previewComponent: 'PreviewDesign1',
                },
                {
                    value: 'full_screening_design2',
                    label: 'Design 2',
                    description: 'Card-based two-column layout.',
                    previewComponent: 'PreviewDesign2',
                },
                {
                    value: 'full_screening_v2',
                    label: 'Design 3',
                    description: 'Soft corporate style with teal accents.',
                    previewComponent: 'PreviewDesign3',
                },
            ],
        };
    },
    created() {
        this.loadCurrentTemplate();
    },
    computed: {
        previewUrl() {
            if (!this.previewModal.option) return '';
            return `${SCREENING_REPORT_TEMPLATE_PREVIEW}/${this.previewModal.option.value}`;
        },
    },
    methods: {
        openPreview(option) {
            this.previewModal.option = option;
            this.previewModal.open = true;
            document.body.style.overflow = 'hidden';
        },
        closePreview() {
            this.previewModal.open = false;
            document.body.style.overflow = '';
        },
        selectFromModal(option) {
            this.selectedTemplate = option.value;
            this.closePreview();
        },
        loadCurrentTemplate() {
            axios.get(SCREENING_REPORT_TEMPLATE_SETTINGS_GET)
                .then(response => {
                    this.selectedTemplate = response.data.template || 'full_screening';
                })
                .catch(error => {
                    console.error('Failed to load template setting:', error);
                });
        },
        saveTemplate() {
            this.saving = true;
            this.savedMessage = '';
            axios.post(SCREENING_REPORT_TEMPLATE_SETTINGS, { template: this.selectedTemplate })
                .then(response => {
                    this.savedMessage = response.data.message || 'Report template saved successfully.';
                    setTimeout(() => { this.savedMessage = ''; }, 3000);
                })
                .catch(error => {
                    this.$toastr.e(
                        error?.response?.data?.message || 'Failed to save template setting.'
                    );
                })
                .finally(() => {
                    this.saving = false;
                });
        },
    },
};
</script>

<style scoped>
.report-template-settings-shell {
    max-width: 900px;
    margin: 2rem auto;
    padding: 0 1rem;
}

/* 2-column grid on wider screens, 1 column on small */
.template-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
}

@media (max-width: 600px) {
    .template-grid {
        grid-template-columns: 1fr;
    }
}

.template-card {
    position: relative;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
    cursor: pointer;
    transition: border-color 0.2s, box-shadow 0.2s;
    background: #fff;
}

.template-card:hover {
    border-color: #a0aec0;
    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}

.template-card:hover .preview-hint {
    opacity: 1;
}

.template-card.selected {
    border-color: #4a7cf6;
    box-shadow: 0 0 0 3px rgba(74, 124, 246, 0.15);
}

/* checkmark badge top-right */
.selected-badge {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 3;
    line-height: 0;
}

/* zoom hint shown on hover */
.preview-hint {
    position: absolute;
    top: 8px;
    left: 8px;
    z-index: 3;
    display: flex;
    align-items: center;
    gap: 4px;
    background: rgba(0,0,0,0.55);
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    padding: 4px 8px;
    border-radius: 20px;
    opacity: 0;
    transition: opacity 0.18s;
    pointer-events: none;
    letter-spacing: 0.3px;
}

/* SVG preview area */
.template-preview {
    width: 100%;
    background: #f8f9fa;
    border-bottom: 1px solid #e2e8f0;
    padding: 8px;
    box-sizing: border-box;
    aspect-ratio: 16 / 9;
    display: flex;
    align-items: stretch;
}

.template-preview > * {
    width: 100%;
    height: 100%;
    display: block;
}

/* name + description strip */
.template-footer {
    padding: 10px 14px 12px;
}

.template-name {
    font-weight: 600;
    font-size: 0.92rem;
    color: #1a202c;
    margin-bottom: 2px;
}

.template-desc {
    font-size: 0.8rem;
    color: #718096;
}

/* ── Modal backdrop ── */
.tpl-modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
}

/* ── Modal box ── */
.tpl-modal {
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 24px 64px rgba(0,0,0,0.22);
    width: 100%;
    max-width: 860px;
    height: 88vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.tpl-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 18px 22px 14px;
    border-bottom: 1px solid #e2e8f0;
    flex-shrink: 0;
}

.tpl-modal-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 3px;
}

.tpl-modal-desc {
    font-size: 0.83rem;
    color: #718096;
}

.tpl-modal-close {
    background: none;
    border: none;
    cursor: pointer;
    color: #718096;
    padding: 2px;
    line-height: 0;
    border-radius: 6px;
    transition: background 0.15s, color 0.15s;
    flex-shrink: 0;
    margin-left: 12px;
}
.tpl-modal-close:hover {
    background: #f0f0f0;
    color: #2d3748;
}

/* Large preview inside modal — iframe fills the area */
.tpl-modal-preview {
    flex: 1;
    overflow: hidden;
    background: #e8e8e8;
    display: flex;
    align-items: stretch;
    min-height: 0;
}

.tpl-modal-iframe {
    width: 100%;
    height: 100%;
    border: none;
    display: block;
    /* scale the report down so it fits without scrolling */
    transform-origin: top left;
}

.tpl-iframe-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    color: #999;
    font-size: 13px;
}

.tpl-modal-footer {
    padding: 14px 22px;
    border-top: 1px solid #e2e8f0;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    flex-shrink: 0;
}

/* ── Fade transition ── */
.modal-fade-enter-active,
.modal-fade-leave-active {
    transition: opacity 0.2s ease;
}
.modal-fade-enter-active .tpl-modal,
.modal-fade-leave-active .tpl-modal {
    transition: transform 0.2s ease;
}
.modal-fade-enter,
.modal-fade-leave-to {
    opacity: 0;
}
.modal-fade-enter .tpl-modal {
    transform: scale(0.95);
}
</style>
